'use client'
import * as React from 'react'
import { trackBookDemo, trackSignUpStart, trackCta } from '@/lib/analytics/ga4'

// Tiny anchor wrapper that fires a GA4 event on click without preventing
// the normal navigation. Use for CTAs we care about funnelling.
//
//   <TrackedLink href="https://app.idukki.io/register" event="sign_up_start" surface="hero">
//     Get started
//   </TrackedLink>
//
// Intent handoff: GA4 alone tells us WHICH surface converted, but the
// destination app never learns that — so nextapp's onboarding can't use it.
// Any href pointing at app.idukki.io gets utm_source/utm_content appended
// (unless the caller already set utm params), so the same `surface` value
// that reaches GA4 also reaches the app as a URL param it can read on
// landing. Purely additive — untouched for any non-app.idukki.io href.
function withIntentParams(href: string, surface: string): string {
  try {
    const url = new URL(href, 'https://app.idukki.io')
    if (!url.hostname.endsWith('app.idukki.io')) return href
    if (!url.searchParams.has('utm_source')) url.searchParams.set('utm_source', 'idukki_web')
    if (!url.searchParams.has('utm_content')) url.searchParams.set('utm_content', surface)
    return url.toString()
  } catch {
    return href
  }
}

type Event = 'sign_up_start' | 'book_demo_click' | 'cta_click'

export function TrackedLink({
  href,
  event,
  surface,
  cta,
  destination,
  children,
  className,
  target,
  rel,
  onClick,
  ...rest
}: {
  href: string
  event: Event
  surface: string
  cta?: string
  destination?: string
  children: React.ReactNode
  className?: string
  target?: string
  rel?: string
  onClick?: (e: React.MouseEvent<HTMLAnchorElement>) => void
} & Omit<React.AnchorHTMLAttributes<HTMLAnchorElement>, 'href' | 'onClick'>) {
  return (
    <a
      href={withIntentParams(href, surface)}
      target={target}
      rel={rel}
      className={className}
      onClick={(e) => {
        try {
          if (event === 'sign_up_start')      trackSignUpStart(surface)
          else if (event === 'book_demo_click') trackBookDemo(surface)
          else                                  trackCta({ cta: cta || 'cta', surface, destination: destination || href })
        } catch { /* analytics never blocks navigation */ }
        onClick?.(e)
      }}
      {...rest}
    >
      {children}
    </a>
  )
}
